import React, { forwardRef, useImperativeHandle, useRef } from "react";
import PropTypes from "prop-types";
import ThumbnailPreview from "DreamCast/Shared/ThumbnailPreview";
import ThumbnailBuilder from "DreamCast/Shared/ThumbnailBuilder";
import { svgDataUrlToPngFile } from "DreamCast/Shared/ThumbnailBuilder/svgToPng";
import { uploadDreamcastThumbnailFile } from "DreamCast/api/media";
import styles from "./MediaThumbnailField.module.css";

export const EMPTY_THUMBNAIL_STATE = Object.freeze({
  thumbnail: null,
  tempFilename: null,
  builderData: null,
  builderOpen: false,
});

// Builds the field state from an API detail payload (thumbnailPath +
// thumbnailBuilderData), matching how the Media page opens an item.
export const thumbnailStateFromDetail = (detail, altText = "Thumbnail") => ({
  thumbnail: detail?.thumbnailPath
    ? { src: detail.thumbnailPath, alt: altText }
    : null,
  tempFilename: null,
  builderData: detail?.thumbnailBuilderData || null,
  builderOpen: Boolean(detail?.thumbnailBuilderData),
});

// Thumbnail editor shared by pages that save media-shaped records (same
// behavior as the Media page): upload/drag an image, or design one in the
// ThumbnailBuilder. Uploads land in Bunny temp storage; the page moves the
// temp file after saving (moveMediaTempImage). Call resolveForSave() via the
// ref before saving — it renders an open builder to PNG and uploads it.
export const MediaThumbnailField = forwardRef(function MediaThumbnailField(
  { value, onChange, onUploadingChange, builderProps },
  ref,
) {
  const builderRef = useRef(null);
  const state = value || EMPTY_THUMBNAIL_STATE;

  const update = (patch) => onChange({ ...state, ...patch });

  const uploadImage = async (file) => {
    if (!file) return;
    update({
      thumbnail: { src: URL.createObjectURL(file), alt: file.name || "Thumbnail" },
      tempFilename: null,
    });
    onUploadingChange(true);
    try {
      const response = await uploadDreamcastThumbnailFile(file, {
        destination: "medias",
      });
      if (response?.data?.thumbnailUrl) {
        onChange({
          ...state,
          thumbnail: { src: response.data.thumbnailUrl, alt: file.name || "Thumbnail" },
          tempFilename: response.data.tempFilename || null,
        });
      }
    } catch {
      // Keep the local preview if the CDN upload fails (same as Media).
    } finally {
      onUploadingChange(false);
    }
  };

  const clear = () => onChange({ ...EMPTY_THUMBNAIL_STATE });

  useImperativeHandle(ref, () => ({
    setBuilderData: (data) => builderRef.current?.setData?.(data),
    // Returns { thumbnail, builderData, tempFilename } ready for the API.
    async resolveForSave() {
      if (!state.builderOpen) {
        return {
          thumbnail: state.thumbnail,
          builderData: state.builderData,
          tempFilename: state.tempFilename,
        };
      }

      const builderData = await builderRef.current?.getSavePayload?.();
      if (!builderData) {
        throw new Error("Unable to render the current thumbnail.");
      }
      if (builderData.isThumbnailEmpty) {
        return { thumbnail: null, builderData: null, tempFilename: null };
      }

      const svgSrc = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
        builderData.svgString,
      )}`;
      onUploadingChange(true);
      try {
        const generatedName = `temp_${Date.now()}_${Math.floor(Math.random() * 10000)}.png`;
        const file = await svgDataUrlToPngFile(svgSrc, generatedName);
        const response = await uploadDreamcastThumbnailFile(file, {
          destination: "medias",
        });
        const url = response?.data?.thumbnailUrl;
        const tempFilename = response?.data?.tempFilename || generatedName;
        if (!url) {
          throw new Error("Failed to upload generated thumbnail.");
        }
        const thumbnail = { src: url, alt: "Generated thumbnail preview" };
        onChange({ ...state, thumbnail, tempFilename, builderData });
        return { thumbnail, builderData, tempFilename };
      } finally {
        onUploadingChange(false);
      }
    },
  }));

  if (state.builderOpen) {
    return (
      <ThumbnailBuilder
        ref={builderRef}
        initialData={state.builderData}
        onDraftChange={(nextData) => update({ builderData: nextData })}
        onDelete={clear}
        className={styles.thumbnailBuilder}
        {...builderProps}
      />
    );
  }

  return (
    <ThumbnailPreview
      thumbnail={state.thumbnail}
      onAddExisting={uploadImage}
      onDrop={uploadImage}
      onEdit={() => update({ builderOpen: true })}
      onDelete={clear}
    />
  );
});

MediaThumbnailField.propTypes = {
  value: PropTypes.shape({
    thumbnail: PropTypes.shape({ src: PropTypes.string, alt: PropTypes.string }),
    tempFilename: PropTypes.string,
    builderData: PropTypes.object,
    builderOpen: PropTypes.bool,
  }),
  onChange: PropTypes.func.isRequired,
  onUploadingChange: PropTypes.func,
  // Extra ThumbnailBuilder props (detailFields, syncedDetails, ...).
  builderProps: PropTypes.object,
};

MediaThumbnailField.defaultProps = {
  value: EMPTY_THUMBNAIL_STATE,
  onUploadingChange: () => {},
  builderProps: undefined,
};

export default MediaThumbnailField;
